//Api paths
import { PARTNER_MANAGEMENT, USER_MANAGEMENT_V2 } from "api/ApiPath";
import { PartnerManagementResponseModel } from "api/repositories/partnerManagement/model/PartnerManagementResponseModel";
//Base model class
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
//Dependency injections
import { FilterInjection } from "app/filters/di/FilterInjection";
import { FilterUseCase } from "app/filters/domain/useCase/FilterUseCase";
import { BalanceHistoryInjection } from "features/balanceHistory/di/BalanceHistoryInjection";
import { injectable } from "tsyringe";
//Utils
import { paramsChecking } from "utils/helpersInTs/helpersInTs";
//Entities
import { BalanceHistoryActionEntity } from "../entities/BalanceHistoryActionEntity";
import { BalanceHistoryDetailEntity } from "../entities/BalanceHistoryDetailEntity";
import { BalanceHistoryRequestEntity } from "../entities/BalanceHistoryRequestEntity";
import { BalanceHistoryResponseEntity } from "../entities/BalanceHistoryResponseEntity";
import { TransactionForRefundRequestEntity } from "../entities/TransactionForRefundRequestEntity";
import { TransactionForRefundResponseEntity } from "../entities/TransactionForRefundResponseEntity";
//Repository
import { IBalanceHistoryRepository } from "../repositories/IBalanceHistoryRepository";

@injectable()
export class BalanceHistoryUseCase {
  private balanceHistoryRepo: IBalanceHistoryRepository;
  private filterUseCase: FilterUseCase;

  constructor() {
    this.balanceHistoryRepo = BalanceHistoryInjection.getBalanceHistoryRepo();
    this.filterUseCase = FilterInjection.getUseCase();
  }

  private checkParams(value: BalanceHistoryRequestEntity) {
    const params = {
      page: value?.page,
      limit: value?.limit,
      phone: paramsChecking(value?.phone, "input"),
      status: paramsChecking(value?.status, "select"),
      type: paramsChecking(value?.type, "select"),
      applicableType: paramsChecking(value?.applicableType, "select"),
      createdBy: paramsChecking(value?.createdBy, "select"),
      updatedBy: paramsChecking(value?.updatedBy, "select"),
      order: value?.order,
    };
    return params;
  }

  private adjustCreateBody(
    body: BalanceHistoryActionEntity,
  ): BalanceHistoryActionEntity {
    const createBody = {
      amount: body?.amount,
      phone: body?.phone,
      type: body?.type,
      applicableType: body?.applicableType,
    };
    return createBody;
  }

  private adjustUpdateBody(
    body: BalanceHistoryActionEntity,
  ): BalanceHistoryActionEntity {
    const updateBody = {
      code: body?.code,
      status: body?.status,
    };
    return updateBody;
  }

  async getBalanceHistoryList(
    params: BalanceHistoryRequestEntity,
  ): Promise<ListBaseApiResponseModel<BalanceHistoryResponseEntity>> {
    const updatedParams = this.checkParams(params);
    return await this.balanceHistoryRepo.getList(updatedParams);
  }

  async getDetail(params: {
    code: string;
  }): Promise<BaseApiResponseModel<BalanceHistoryDetailEntity>> {
    return await this.balanceHistoryRepo.getDetail(params);
  }

  async getTransactionForRefund(
    params: TransactionForRefundRequestEntity,
  ): Promise<ListBaseApiResponseModel<TransactionForRefundResponseEntity>> {
    return await this.balanceHistoryRepo.getTransactionForRefund(params);
  }

  async updateBalanceTicket(
    body: BalanceHistoryActionEntity,
  ): Promise<BaseApiResponseModel<BalanceHistoryDetailEntity>> {
    const updateBody = this.adjustUpdateBody(body);
    return await this.balanceHistoryRepo.updateTicket(updateBody);
  }

  async createBalanceTicket(
    body: BalanceHistoryActionEntity,
  ): Promise<BaseApiResponseModel<BalanceHistoryDetailEntity>> {
    const createBody = this.adjustCreateBody(body);
    return await this.balanceHistoryRepo.createTicket(createBody);
  }

  async getStaffList(params: BaseUserRequestModel) {
    const finalPath = USER_MANAGEMENT_V2.USER_LIST_V1;
    return await this.filterUseCase.fetchListAndPagingWithFeature<
      BaseUserRequestModel,
      BaseUserResponseModel
    >({ path: finalPath, params });
  }

  async getPartnerList(params: BaseFilterListRequestModel) {
    const finalPath = PARTNER_MANAGEMENT.LIST;
    return await this.filterUseCase.fetchListAndPagingWithFeature<
      typeof params,
      PartnerManagementResponseModel
    >({ path: finalPath, params });
  }
}
